/**
 * Shared utilities for booking engine components
 * Contains reusable logic that doesn't belong to specific components
 */

import { get_config } from "@/utilities/";
import { toDate } from "@liveflight/utilities/dates";

/**
 * Calculate minimum departure date based on business rules
 * - Adds configured minDays offset
 * - Adds 1 day if current time is after 13:00
 * - Respects minCheckin configuration if set
 * @returns {Date} Minimum departure date
 */
export function calculateMinDepartureDate() {
  let min_date = new Date();
  min_date.setHours(0, 0, 0, 0);

  try {
    const minCheckin = get_config("guiSettings.minCheckin", undefined);
    if (minCheckin) {
      const checkinDate = toDate(minCheckin);
      if (min_date < checkinDate) {
        min_date = checkinDate;
      }
    }

    const minDays = get_config("guiSettings.minDays", 0);
    min_date.setDate(min_date.getDate() + minDays);

    if (new Date().getHours() > 13) {
      min_date.setDate(min_date.getDate() + 1);
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("booking-engine.calculateMinDepartureDate", e);
  }

  return min_date;
}

/**
 * Calculate maximum date for booking (1 year from today)
 * @returns {Date} Maximum date
 */
export function calculateMaxDate() {
  let today = new Date();
  let max_date = new Date(today);
  max_date.setDate(today.getDate() + 365);
  return max_date;
}

/**
 * Build travelers array from occupancy data
 * Converts adults + children (with ages) into Amadeus API format
 * @param {Object} occupancy - Occupancy data
 * @param {number} occupancy.adults - Number of adults
 * @param {Array<number>} occupancy.children - Array of children ages
 * @returns {Array<Object>} Travelers array in Amadeus format
 */
export function buildTravelersArray(occupancy) {
  let travelers = [];
  let id = 1;
  let associated_adult_id = 1;

  // Add adults
  for (let a = 1; a <= occupancy.adults; a++) {
    travelers.push({
      id: id,
      travelerType: "ADULT",
      fareOptions: ["STANDARD"],
    });
    id++;
  }

  // Add children/infants
  occupancy.children.forEach((childAge) => {
    let child = {
      id: id,
      travelerType: "CHILD",
      fareOptions: ["STANDARD"],
    };

    // Children under 2 are held infants
    if (childAge < 2) {
      child.travelerType = "HELD_INFANT";
      child.associatedAdultId = associated_adult_id;
      associated_adult_id++;
    }

    travelers.push(child);
    id++;
  });

  return travelers;
}

/**
 * Strip HTML tags from a string
 * @param {string} str - String with potential HTML tags
 * @returns {string} String without HTML tags
 */
export function stripHtmlTags(str) {
  if (!str) return "";
  return str.replace(/<[^>]*>?/gm, "");
}
